/* eslint-disable @typescript-eslint/ban-ts-comment */

'use client';

import { useEffect, useRef, useState } from 'react';
import { HightLightResponse, MatrixComponent } from 'matrix';
import { useSearchParams } from 'next/navigation';
import searchCategoryType from 'services/searchCategoryType/searchClient';
import { DateFormatter, IconLocation } from '@sympla/sympla-components';
import IconFlatCalendar from 'assets/icons/ic-flat-calendar.svg';
import { useFeature } from '@growthbook/growthbook-react';
import { HightLightWrapperSkeleton } from '../Matrix/components/CarouselWrapper/CarouselWrapper.skeleton';
import * as styles from './HightLightCarousel.css';
import Carousel from './Carousel/Carousel';
import { analytics, analyticsEvents } from 'lib/analytics';

interface HomeHightLightCarouselProps {
  component: MatrixComponent;
  matrizTemplate: string;
  prefixTrackingName: string;
}

function HomeHightLightCarousel({
  component,
  matrizTemplate,
  prefixTrackingName
}: HomeHightLightCarouselProps) {
  const searchParams = useSearchParams();
  const [isLoading, setIsLoading] = useState(false);
  const previousIndexRef = useRef(0);
  const discoveryHomeBannerExcludedEventsFeature = useFeature(
    'discovery-home-banner-excluded-events'
  );
  const discoveryHomeBannerExcludedEvents =
    discoveryHomeBannerExcludedEventsFeature?.value?.eventIds || [];

  const [highLightComponents, setHighLightComponents] = useState<
    HightLightResponse[] | []
    // @ts-ignore
  >(component.response?.data || []);
  const [componentActive, setComponentActive] = useState<HightLightResponse>();
  const [textClassesFortransform, setTextClassesFortransform] = useState<
    'exitLeft' | 'exitRight'
  >('exitLeft');

  useEffect(() => {
    if (component && !component?.response) {
      const { service, service_params } = component;
      const dt = searchParams?.get('dt') as string | undefined;
      const p = searchParams?.get('p') as string | undefined;

      setIsLoading(true);
      searchCategoryType({
        ...service_params,
        dt,
        p,
        service
      })
        .then((response) => {
          if (response) {
            setHighLightComponents(response.data);
            setComponentActive(response.data[0]);
          }
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, [component]);

  function handleChangeSlide(index: number | undefined) {
    if (index !== undefined) {
      const oldIndex = previousIndexRef.current;
      const isForward =
        (index === 0 && oldIndex === highLightComponents.length - 1) ||
        index > oldIndex;
      setTextClassesFortransform(isForward ? 'exitRight' : 'exitLeft');
      setComponentActive(highLightComponents[index]);
      previousIndexRef.current = index;
    }
  }

  function handleClickSlide(slide: HightLightResponse, index: number) {
    analytics.sendEvent(analyticsEvents.home_highlight_events_clicked, {
      event_id: slide.event_id,
      event_name: slide.title,
      item_highlight_category: (
        slide as unknown as Record<'event_type', string>
      ).event_type,
      item_highlight_position: index + 1
    });
  }

  if (isLoading) {
    return <HightLightWrapperSkeleton />;
  }

  return (
    <div
      className={styles.container}
      data-testid="home-highlight-carousel-container"
    >
      <Carousel
        slides={highLightComponents.filter(
          (item) =>
            !discoveryHomeBannerExcludedEvents?.includes(
              item.event_id as number
            )
        )}
        id="hight-light-home"
        handleChangeSlide={(idx) => handleChangeSlide(idx)}
        handleClickSlide={handleClickSlide}
        componentTemplate={component.uuid}
        prefixTrackingName={prefixTrackingName}
        matrizTemplate={matrizTemplate}
        templateType={component.response_type}
      />
      {componentActive && (
        <div
          className={`${styles.infoContainer} ${styles.animationState[textClassesFortransform]}`}
          key={componentActive.title}
          data-testid="home-highlight-carousel-info"
        >
          <h3
            className={styles.infoTitle}
            data-testid="home-highlight-carousel-title"
          >
            {componentActive.title}
          </h3>
          {componentActive.location &&
            componentActive.start_date_formats &&
            componentActive.end_date_formats && (
              <div
                className={styles.infoExtraContainer}
                data-testid="home-highlight-carousel-extra-info"
              >
                <span
                  className={styles.infoExtraText}
                  data-testid="home-highlight-carousel-location"
                >
                  <IconLocation />
                  {componentActive.location.city}
                  {' - '}
                  {componentActive.location.state}
                </span>
                <span
                  className={`${styles.infoExtraText} stroke-svg`}
                  data-testid="home-highlight-carousel-date"
                >
                  <IconFlatCalendar />
                  <DateFormatter
                    variant="neutral"
                    startDate={componentActive.start_date_formats.pt}
                    endDate={componentActive.end_date_formats.pt}
                    rawStartDate={componentActive.startDate}
                    rawEndDate={componentActive.endDate}
                  />
                </span>
              </div>
            )}
        </div>
      )}
    </div>
  );
}

export default HomeHightLightCarousel;
